import React from 'react';
import { findDOMNode } from 'react-dom';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import styled from 'src/ui/library/styled';

const styles = (theme, props) => {
  const label = props.label || '';
  return {
    textField: {
      backgroundColor: theme.colors.eyGrey5,
      height: label.length > 41 ? 'auto' : '48px',
      maxWidth: '300px',
      border: '1px solid transparent',
    },
    textFieldFocus: {
      border: `1px solid ${theme.colors.eyGrey6}`,
    },
    inputRoot: {
      marginTop: label.length > 41 ? '40px !important' : '16px',
      paddingLeft: '7px',
      color: theme.colors.eyConfidentGrey,
    },
    inputDom: {},
    formLabel: {
      color: theme.colors.eyConfidentGrey,
      paddingLeft: '8px',
    },
    formControl: {
      transform: 'translate(0, 16px) scale(1)',
    },
    shrink: {
      transform: 'translate(0, 8px) scale(0.75)',
      marginBottom: '8px',
      color: theme.colors.eyGrey1,
    },
    showPassword: {
      fontFamily: 'caption',
    },
    visibilityIcon: {
      position: 'relative',
      top: '-8px',
    },
    visibilityIconRoot: {
      color: theme.colors.eyGrey2,
    },
    focused: {
      '&$focused': {
        color: theme.colors.eyGrey1,
      },
    },
    hasError: {
      border: `solid 1px ${theme.colors.red}`,
    },
  };
};

class TextInputBase extends React.Component {
  static propTypes = {
    /** Classname to be applied to <TextField> */
    className: PropTypes.string,
    /** If `true`, the input will not have an underline. */
    disableUnderline: PropTypes.bool,
    /** If `true`, password will be visible */
    showPassword: PropTypes.bool,
    /** If `true`, textfield will be in error state */
    hasError: PropTypes.bool,
    /** Override or extend default styles */
    styles: PropTypes.object,
    /** Name attribute of the `input` element. */
    name: PropTypes.string,
    /** Value of `input` element */
    value: PropTypes.string,
    /** Type of the input element. It should be a valid HTML5 input type. */
    type: PropTypes.string,
    /** Text used for input label */
    label: PropTypes.string,
    /** If `true`, the label is displayed as required. */
    required: PropTypes.any,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,
  };

  static defaultProps = {
    styles: {},
    hasError: false,
    disableUnderline: true,
    showPassword: false,
  };

  state = {
    name: this.props.name,
    value: this.props.value || '',
    focused: this.props.focused || false,
    autofillFocus: null,
    passwordVisible: this.props.showPassword || false,
  };

  componentDidMount() {
    if (this.inputRef) {
      this.inputRef.addEventListener('animationstart', this.onAnimationStart, false);
    }
  }

  componentWillUnmount() {
    if (this.inputRef) {
      this.inputRef.removeEventListener('animationstart', this.onAnimationStart, false);
    }
  }

  // Chrome when using autofill looks really nasty. The only way around this is to
  // bind an animation event and listen for it. Once it triggers, auto focus the TextField
  onAnimationStart = ({ target, animationName }) => {
    if (animationName === 'onAutoFillStart') {
      this.setState({ autofillFocus: true });
    }
  };

  handleOnChange = evt => {
    this.setState({ value: evt.target.value });
    return (this.props.onChange && this.props.onChange(evt)) || true;
  };

  handleOnFocus = status => {
    this.setState({ focused: status });
  };

  handleMouseDownPassword = event => {
    event.preventDefault();
  };

  handleClickShowPassword = () => {
    this.setState({ passwordVisible: !this.state.passwordVisible });
  };

  setInputRef = el => {
    if (this.props.inputRef) this.props.inputRef(el);

    this.inputRef = findDOMNode(el);
  };

  render() {
    const {
      classes,
      helperText,
      styles,
      type,
      hasError,
      className,
      showPassword,
      onChange,
      onFocus,
      disableUnderline,
      inputRef,
      InputLabelProps,
      InputProps,
      label,
      required,
      ...otherProps
    } = this.props;

    const textClassName = classNames(
      {
        [classes.textField]: true,
        [classes.textFieldFocus]: this.state.focused,
        [classes.hasError]: this.props.hasError,
      },
      className,
    );

    let inputType = type;

    if (inputType === 'password' && this.state.passwordVisible) inputType = 'text';
    return (
      <TextField
        helperText={helperText}
        type={inputType}
        className={textClassName}
        onChange={this.handleOnChange}
        onFocus={this.handleOnFocus.bind(null, true)}
        onBlur={this.handleOnFocus.bind(null, false)}
        fullWidth={true}
        inputRef={el => this.setInputRef(el)}
        InputProps={{
          disableUnderline,
          className: classes.inputRoot,
          classes: {
            input: classes.inputDom,
          },
          endAdornment:
            this.props.type === 'password' ? (
              <InputAdornment position="end" className={classes.visibilityIcon}>
                <IconButton
                  aria-label="Toggle password visibility"
                  onClick={this.handleClickShowPassword}
                  onMouseDown={this.handleMouseDownPassword}>
                  {this.state.passwordVisible ? (
                    <VisibilityOff className={classNames(classes.visibilityIconRoot, classes.showPassword)} />
                  ) : (
                    <Visibility className={classes.visibilityIconRoot} />
                  )}
                </IconButton>
              </InputAdornment>
            ) : null,
          ...InputProps,
        }}
        InputLabelProps={{
          ...(this.state.autofillFocus ? { focused: true } : {}),
          ...(this.state.autofillFocus || this.state.value ? { shrink: true } : {}),
          classes: {
            formControl: classes.formControl,
            shrink: classes.shrink,
          },
          FormLabelClasses: {
            root: classes.formLabel,
            focused: classes.focused,
          },
          ...InputLabelProps,
        }}
        FormHelperTextProps={{
          classes: {
            root: classes.formHelperText,
          },
          error: hasError,
        }}
        // value={this.state.value}
        label={ required ? label+ intl.get('questionnaire.requiredLabel') : label }
        {...otherProps}
      />
    );
  }
}

export { TextInputBase };
export default styled(TextInputBase)(styles);



// WEBPACK FOOTER //
// ./src/ui/library/TextInput/TextInputBase.js